import {
  createColumnHelper,
  getCoreRowModel,
  type Row,
  useReactTable,
} from '@tanstack/react-table'
import { Pencil, UserPlusIcon } from 'lucide-react'
import React, { useEffect, useState } from 'react'

import RequestAQuoteButton from 'components/partners/RequestAQuoteButton'
import { type MetadataConfiguration } from 'components/quoting/types'
import { ButtonLink, IconLink, TextButton } from 'slab'
import { useGetQuotingProspects } from 'utils/web_api'
import { type QuotingProspect } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { CreateOrEditProspect } from './CreateOrEditProspect'
import { ProspectOwnerLink } from './ProspectOwnerLink'
import { getLocaleDateString } from '../../utils'
import TanstackTable, {
  DEFAULT_PAGE_SIZE,
  type PaginationMetadata,
} from '../reusable/table/TanstackTable'

function RowAction ({
  isForAdminView,
  prospect,
  showEIN,
  metadataConfiguration,
}: {
  isForAdminView: boolean;
  prospect: QuotingProspect;
  showEIN: boolean;
  metadataConfiguration?: MetadataConfiguration;
}) {
  return (
    <CreateOrEditProspect
      prospect={prospect}
      isCreate={false}
      isForAdminView={isForAdminView}
      showEIN={showEIN || isForAdminView}
      triggerChildren={
        <button
          className='w-auto hover:text-thatch-purple-primary'
          title='Edit prospect'
          aria-label='Edit prospect'
        >
          <Pencil size={16} />
        </button>
      }
      metadataConfiguration={metadataConfiguration}
    />
  )
}

const columnHelper = createColumnHelper<QuotingProspect>()

// Define getColumns function
const getColumns = (showEIN: boolean, quotingV2EditProspectPath?: string, metadataConfiguration?: MetadataConfiguration) => [
  columnHelper.accessor('name', {
    header: 'Name',
  }),
  columnHelper.accessor(
    (row) => ({
      content: <ProspectOwnerLink prospect={row} />,
    }),
    {
      header: 'Owner',
      id: 'owner',
      cell: (info) => info.getValue().content,
    }
  ),
  // This column is for broker admins. Thatch admins will be shown the <ProspectOwnerLink /> component
  columnHelper.accessor('owner_email', {
    header: 'Owner',
  }),
  columnHelper.accessor('email', {
    header: 'Email',
  }),
  columnHelper.accessor('created_at', {
    header: 'Date created',
    cell: (info) =>
      info.getValue()
        ? getLocaleDateString(new Date(info.getValue()))
        : undefined,
  }),
  columnHelper.accessor('ein', {
    header: 'EIN',
  }),
  {
    accessorKey: 'admin_actions',
    header: '',
    cell: ({ row }: { row: Row<QuotingProspect> }) => (
      <RowAction isForAdminView prospect={row.original} showEIN={showEIN} metadataConfiguration={metadataConfiguration} />
    ),
  },
  {
    accessorKey: 'tfb_actions',
    header: '',
    cell: ({ row }: { row: Row<QuotingProspect> }) => (
      <RowAction isForAdminView={false} prospect={row.original} showEIN={showEIN} metadataConfiguration={metadataConfiguration} />
    ),
  },
  {
    accessorKey: 'quoting_v2_actions',
    header: '',
    cell: ({ row }: { row: Row<QuotingProspect> }) => (
      <IconLink
        href={quotingV2EditProspectPath?.replace(':id', row.original.id) || ''}
        label='Edit prospect'
        icon={Pencil}
      />
    ),
  }
]

type ProspectsListProps = {
  currentUserId?: string;
  currentPartnerId?: string;
  isForAdminView?: boolean;
  pageParam?: number | null;
  showEIN?: boolean;
  showOwnerEmail?: boolean;
  quotingV2NewProspectPath?: string;
  quotingV2EditProspectPath?: string;
  metadataConfiguration?: MetadataConfiguration;
}

export function ProspectsListImpl ({
  currentUserId,
  currentPartnerId,
  isForAdminView = false,
  pageParam,
  showEIN = false,
  showOwnerEmail = false,
  quotingV2NewProspectPath,
  quotingV2EditProspectPath,
  metadataConfiguration,
}: ProspectsListProps) {
  const { assign } = useWindowLocation()

  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()
  const [pagination, setPagination] = useState({
    pageIndex: pageParam || 1,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  // Get columns by calling the function, adjusting the passed prop
  const columns = React.useMemo(() => {
    return getColumns(showEIN || isForAdminView, quotingV2EditProspectPath, metadataConfiguration)
  }, [showEIN, isForAdminView, quotingV2EditProspectPath, metadataConfiguration])

  const { data, isLoading, isError } = useGetQuotingProspects({
    'page[number]': pagination.pageIndex,
    'page[size]': pagination.pageSize,
    owner_id: isForAdminView ? undefined : currentUserId,
    partner_id: (isForAdminView || !currentPartnerId) ? undefined : currentPartnerId,
  })

  const table = useReactTable({
    data: data?.data || [],
    columns,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility: {
        name: true,
        owner: isForAdminView,
        owner_email: showOwnerEmail && !isForAdminView,
        email: true,
        ein: showEIN || isForAdminView,
        created_at: true,
        tfb_actions: !isForAdminView && !quotingV2EditProspectPath,
        admin_actions: isForAdminView && !quotingV2EditProspectPath,
        quoting_v2_actions: !!quotingV2EditProspectPath,
      },
      pagination,
    },
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data?.pagination)
    }
  }, [data?.pagination])

  useEffect(() => {
    const pageURL = new URL(window.location.href)
    const pageIndex = pagination.pageIndex.toString()
    pageURL.searchParams.set('page', pageIndex)
    window.history.pushState({}, '', pageURL.toString())
  }, [pagination.pageIndex])

  return (
    <div className='w-full h-full flex flex-col'>
      <div className='flex justify-between items-center mb-6'>
        <h1 className='heading-lg'>Prospects</h1>
        <>
          {quotingV2NewProspectPath
            ? (
              <div className='flex flex-col gap-3 items-end'>
                <ButtonLink
                  href={quotingV2NewProspectPath}
                  variant='primary'
                  leftIcon={UserPlusIcon}
                >
                  Create new prospect
                </ButtonLink>
                <CreateOrEditProspect
                  isForAdminView={isForAdminView}
                  isCreate
                  showEIN={showEIN || isForAdminView}
                  metadataConfiguration={metadataConfiguration}
                  triggerChildren={
                    <TextButton color='purple'>
                      use old tool
                    </TextButton>
            }
                />
              </div>
              )
            : (
              <RequestAQuoteButton
                location='top_nav'
                canSelfServeQuotes
              />
              )}
        </>
      </div>
      <TanstackTable
        table={table}
        isLoading={isLoading}
        isError={isError}
        noDataMessage='There are no prospects to display.'
        onRowClick={({ event, row }) => {
          // prevent going to the details page when the user is interacting with the menu item or the dialog
          let currentElement: HTMLElement | null = event.target as HTMLElement
          let menuTriggered = false

          while (currentElement && currentElement !== event.currentTarget) {
            if (
              currentElement.getAttribute('aria-label') === 'Edit prospect' ||
              currentElement.getAttribute('role') === 'dialog' ||
              !!currentElement.getAttribute('data-state') || // for the overlay of the dialog
              currentElement.getAttribute('aria-label') ===
                'Owner inspect link' ||
              currentElement.getAttribute('aria-label') === 'External link'
            ) {
              menuTriggered = true
              break
            }
            currentElement = currentElement.parentElement
          }

          if (!menuTriggered) {
            const recordId = row.original.id
            assign(`/${
              isForAdminView ? 'admin' : 'broker'
            }/prospects/${recordId}`)
          } else {
            event.stopPropagation()
          }
        }}
        paginationLabelSingular='prospect'
        paginationMetadata={paginationMetadata}
        onNextPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.next_page || 0,
          })}
        onPreviousPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.prev_page || 0,
          })}
      />
    </div>
  )
}

const ProspectsList = ({
  currentUserId,
  currentPartnerId,
  isForAdminView,
  pageParam,
  showEIN,
  showOwnerEmail,
  quotingV2NewProspectPath,
  quotingV2EditProspectPath,
  metadataConfiguration,
}: ProspectsListProps) => {
  return (
    <ProspectsListImpl
      currentUserId={currentUserId}
      currentPartnerId={currentPartnerId}
      isForAdminView={isForAdminView}
      pageParam={pageParam}
      showEIN={showEIN}
      showOwnerEmail={showOwnerEmail}
      quotingV2NewProspectPath={quotingV2NewProspectPath}
      quotingV2EditProspectPath={quotingV2EditProspectPath}
      metadataConfiguration={metadataConfiguration}
    />
  )
}

export default ProspectsList
